import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Content,
  Header,
  InfoCard,
  Page,
  Progress,
  ProxiedSignInPage,
  ResponseErrorPanel,
  StatusError,
  StatusPending,
} from '@backstage/core-components';
import {
  configApiRef,
  IdentityApi,
  SignInPageProps,
  useApi,
} from '@backstage/core-plugin-api';
import { SignInPageBlueprint } from '@backstage/plugin-app-react';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { useNavigate } from 'react-router-dom';
import { BackendDbStatus, fetchBackendDbStatus } from '../../lib/backendHealth';
import {
  clearSsoLoginPending,
  clearSsoSession,
  getToken,
  isSsoEnabled,
  isTokenExpired,
  markSsoLoginPending,
  readSsoConfig,
  redirectToSSO,
  setToken,
  ssoAuthHeaders,
  validateToken,
} from '../../lib/sso';

/** En Sign out borra sso_token para no re-entrar al catálogo automáticamente. */
function withSsoSignOut(identity: IdentityApi): IdentityApi {
  return {
    getProfileInfo: () => identity.getProfileInfo(),
    getBackstageIdentity: () => identity.getBackstageIdentity(),
    getCredentials: () => identity.getCredentials(),
    signOut: async () => {
      clearSsoSession();
      await identity.signOut();
    },
  };
}

const useStyles = makeStyles(
  {
    container: {
      padding: 0,
      listStyle: 'none',
    },
    item: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      maxWidth: '400px',
      margin: 0,
      padding: 0,
    },
    statusBox: {
      marginBottom: 16,
    },
  },
  { name: 'ImjSignInPage' },
);

function SsoRefreshError({
  error,
  onBack,
}: {
  error?: Error;
  onBack: () => void;
}) {
  useEffect(() => {
    clearSsoLoginPending();
  }, []);

  const raw = error?.message ?? '';
  const hints: string[] = [];
  if (/404/i.test(raw)) {
    hints.push(
      'El backend no tiene el provider SSO activo (ruta /api/auth/sso/refresh). Revisa logs del backend: "Configuring auth provider: sso" o "Skipping sso".',
    );
    hints.push(
      'Si acabas de cambiar auth/DB, reinicia el backend. En local DB_HOST debe ser localhost (no backstage-db).',
    );
  }
  if (/failed to fetch|networkerror|load failed/i.test(raw)) {
    hints.push(
      'No se pudo contactar el API. Revisa BACKEND_BASE_URL (sin :7007 detrás de Traefik) y que el servicio backend esté sano.',
    );
  }

  return (
    <Page themeId="home">
      <Header title="Error de SSO" />
      <Content>
        {error && <ResponseErrorPanel error={error} />}
        {hints.length > 0 && (
          <Typography variant="body2" paragraph>
            {hints.join(' ')}
          </Typography>
        )}
        <Button color="primary" variant="contained" onClick={onBack}>
          Volver al login
        </Button>
      </Content>
    </Page>
  );
}

function SsoCompleteSignIn({
  onSignInSuccess,
  onBack,
}: SignInPageProps & { onBack: () => void }) {
  const configApi = useApi(configApiRef);
  const sso = useMemo(() => readSsoConfig(configApi), [configApi]);

  return (
    <ProxiedSignInPage
      onSignInSuccess={identity => {
        clearSsoLoginPending();
        if (window.location.pathname !== '/') {
          window.history.replaceState({}, document.title, '/');
        }
        onSignInSuccess(withSsoSignOut(identity));
      }}
      provider="sso"
      headers={() => ssoAuthHeaders(sso)}
      ErrorComponent={({ error }) => (
        <SsoRefreshError
          error={error}
          onBack={() => {
            clearSsoSession();
            onBack();
          }}
        />
      )}
    />
  );
}

function DbStatusPanel({
  status,
  checking,
  onRetry,
}: {
  status?: BackendDbStatus;
  checking: boolean;
  onRetry: () => void;
}) {
  // Solo visible al comprobar o si hay error; si está OK no se muestra.
  if (status?.ok && !checking) {
    return null;
  }

  return (
    <InfoCard
      title="Estado del sistema"
      action={
        <Button size="small" onClick={onRetry} disabled={checking}>
          Reintentar
        </Button>
      }
    >
      {(checking || !status) && (
        <StatusPending>Comprobando backend y base de datos…</StatusPending>
      )}
      {status && !status.ok && !checking && (
        <>
          <StatusError>
            Sin conexión a la base de datos
            {status.target ? ` (${status.target})` : ''}
          </StatusError>
          {status.error && (
            <Typography variant="body2" color="error" paragraph>
              {status.error}
            </Typography>
          )}
          {status.hints?.map(hint => (
            <Typography key={hint} variant="body2" paragraph>
              {hint}
            </Typography>
          ))}
          <Typography variant="body2" color="textSecondary">
            Corrige la configuración y pulsa Reintentar antes de iniciar sesión.
          </Typography>
        </>
      )}
    </InfoCard>
  );
}

function SignInRouter(props: SignInPageProps) {
  const configApi = useApi(configApiRef);
  const navigate = useNavigate();
  const sso = useMemo(() => readSsoConfig(configApi), [configApi]);
  const classes = useStyles();
  const ssoOn = isSsoEnabled(sso);
  const backendBaseUrl = configApi.getString('backend.baseUrl');

  const [phase, setPhase] = useState<'loading' | 'choose' | 'sso-complete'>(
    'loading',
  );
  const [callbackError, setCallbackError] = useState<Error | undefined>();
  const [dbStatus, setDbStatus] = useState<BackendDbStatus | undefined>();
  const [dbChecking, setDbChecking] = useState(true);

  const refreshDbStatus = useCallback(async () => {
    setDbChecking(true);
    const status = await fetchBackendDbStatus(backendBaseUrl);
    setDbStatus(status);
    setDbChecking(false);
    return status;
  }, [backendBaseUrl]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const status = await refreshDbStatus();
      if (cancelled) return;

      if (!ssoOn) {
        setCallbackError(
          new Error(
            'SSO no configurado. Define SSO_APPLICATION_ID en el .env y reinicia.',
          ),
        );
        setPhase('choose');
        return;
      }

      const params = new URLSearchParams(window.location.search);
      const tokenParam = params.get('token');
      const errorParam = params.get('error');

      if (errorParam) {
        clearSsoLoginPending();
        setCallbackError(new Error(errorParam));
        setPhase('choose');
        return;
      }

      if (tokenParam) {
        setToken(tokenParam);
        markSsoLoginPending();
      }

      if (
        window.location.pathname === '/callback' ||
        tokenParam ||
        params.get('sso') === '1'
      ) {
        navigate('/', { replace: true });
      }

      // Sin DB no continuamos al refresh SSO
      if (!status.ok) {
        setPhase('choose');
        return;
      }

      const token = getToken();
      if (token && !isTokenExpired(token)) {
        const valid = await validateToken(sso, token);
        if (cancelled) return;
        if (valid) {
          setPhase('sso-complete');
          return;
        }
        clearSsoSession();
      } else if (token) {
        clearSsoSession();
      }

      if (!cancelled) setPhase('choose');
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [sso, ssoOn, navigate, refreshDbStatus]);

  if (phase === 'loading') {
    return (
      <Page themeId="home">
        <Header title={configApi.getString('app.title')} />
        <Content>
          <Progress />
          <Typography variant="body2">
            Comprobando conexión a la base de datos…
          </Typography>
        </Content>
      </Page>
    );
  }

  if (phase === 'sso-complete') {
    return <SsoCompleteSignIn {...props} onBack={() => setPhase('choose')} />;
  }

  const canSignIn = ssoOn && !!dbStatus?.ok && !dbChecking;

  return (
    <Page themeId="home">
      <Header title={configApi.getString('app.title')} />
      <Content>
        {(dbChecking || (dbStatus && !dbStatus.ok)) && (
          <div className={classes.statusBox}>
            <DbStatusPanel
              status={dbStatus}
              checking={dbChecking}
              onRetry={() => {
                void refreshDbStatus();
              }}
            />
          </div>
        )}
        {callbackError && <ResponseErrorPanel error={callbackError} />}
        <Grid
          container
          justifyContent="flex-start"
          spacing={2}
          component="ul"
          classes={{ container: classes.container }}
        >
          <Grid component="li" item classes={{ item: classes.item }}>
            <InfoCard
              variant="fullHeight"
              title="Acceso con SSO corporativo"
              actions={
                <Button
                  color="primary"
                  variant="outlined"
                  disabled={!canSignIn}
                  onClick={() => redirectToSSO(sso)}
                >
                  Iniciar sesión
                </Button>
              }
            >
              <Typography variant="body1">
                Iniciar sesión con SSO (sso.obp.mx)
              </Typography>
            </InfoCard>
          </Grid>
        </Grid>
      </Content>
    </Page>
  );
}

export const signInPage = SignInPageBlueprint.make({
  params: {
    loader: async () => props => <SignInRouter {...props} />,
  },
});
